<template>
	<v-dialog v-model="dialogOpen" max-width="800px" persistent scrollable>
		<v-card>
			<v-card-title class="d-flex justify-space-between align-center modal-header-primary">
				<span class="text-h6 modal-header-primary-text">Télécharger les documents</span>
				<v-btn icon variant="text" @click="closeDialog" class="modal-header-primary-icon">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-card-title>
			<v-card-text>
				<div class="mb-4">
					<p class="text-body-2 text-grey mb-4">
						Sélectionnez les documents que vous souhaitez télécharger. Vous pouvez en choisir plusieurs.
					</p>
					<v-list>
						<v-list-item
							v-for="doc in availableDocuments"
							:key="doc.id"
							class="mb-2"
						>
							<template #prepend>
								<v-checkbox
									v-model="selectedDocuments"
									:value="doc.id"
									color="primary"
									hide-details
									density="compact"
								/>
							</template>
							<v-list-item-title>{{ doc.title }}</v-list-item-title>
							<v-list-item-subtitle>{{ doc.description }}</v-list-item-subtitle>
							<template #append>
								<v-btn
									size="small"
									variant="text"
									color="primary"
									@click="previewDocument(doc.id)"
								>
									<v-icon left size="small">mdi-eye</v-icon>
									Preview
								</v-btn>
							</template>
						</v-list-item>
					</v-list>
				</div>

				<v-expand-transition>
					<v-card
						v-if="selectedDocuments.includes('tableau')"
						variant="outlined"
						class="mb-2"
						style="border-radius: 3px;"
					>
						<v-card-text>
							<div class="text-subtitle-2 mb-3 d-flex align-center">
								<v-icon size="small" class="mr-1" color="primary">mdi-account-group</v-icon>
								Signataires du tableau de délibération
							</div>
							<p class="text-caption text-medium-emphasis mb-3">
								Décochez les personnes à exclure du document. Le président du jury peut être laissé vide pour remplissage manuel.
							</p>

							<div v-if="tableauSignaturesLoading" class="d-flex align-center py-4">
								<v-progress-circular indeterminate size="24" width="2" class="mr-3" />
								<span class="text-body-2 text-medium-emphasis">Chargement des signataires…</span>
							</div>

							<v-alert
								v-else-if="tableauSignaturesError"
								type="warning"
								variant="tonal"
								density="compact"
								class="mb-0"
							>
								{{ tableauSignaturesError }}
							</v-alert>

							<template v-else>
								<v-checkbox
									v-model="includePresident"
									label="Président du jury"
									density="compact"
									hide-details
									color="primary"
									class="mb-2"
								>
									<template #label>
										<div>
											<div class="font-weight-medium">Président du jury</div>
											<div v-if="presidentDisplayName" class="text-caption text-medium-emphasis">
												{{ presidentDisplayName }}
											</div>
										</div>
									</template>
								</v-checkbox>

								<v-divider class="my-3" />

								<div class="text-body-2 font-weight-medium mb-2">Professeurs signataires</div>
								<div v-if="signatureMemberOptions.length === 0" class="text-caption text-medium-emphasis">
									Aucun professeur signataire trouvé.
								</div>
								<v-checkbox
									v-for="member in signatureMemberOptions"
									:key="member.personnelId"
									v-model="selectedPersonnelIds"
									:value="member.personnelId"
									density="compact"
									hide-details
									color="primary"
									class="mb-1"
								>
									<template #label>
										<div>
											<div class="font-weight-medium">{{ member.name }}</div>
											<div v-if="member.matieres" class="text-caption text-medium-emphasis">
												{{ member.matieres }}
											</div>
										</div>
									</template>
								</v-checkbox>
							</template>
						</v-card-text>
					</v-card>
				</v-expand-transition>
			</v-card-text>
			<v-card-actions class="d-flex justify-center">
				<v-btn variant="outlined" color="secondary" @click="closeDialog">Annuler</v-btn>
				<v-btn 
					class="mydit-btn" 
					@click="downloadSelectedDocuments" 
					:loading="downloading"
					:disabled="selectedDocuments.length === 0 || tableauSignaturesLoading"
				>
					<v-icon left color="white">mdi-download</v-icon>
					Télécharger ({{ selectedDocuments.length }})
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>

	<!-- Modal de prévisualisation -->
	<DeliberationDocumentPreview
		v-model="previewDialogOpen"
		:document-type="previewDocumentType"
		:deliberation="deliberation"
		:tableau-export-filter="previewTableauExportFilter"
	/>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { Deliberation } from '@/types/deliberation'
import type {
	DeliberationDocumentsDownloadPayload,
	TableauDeliberationExportFilter,
} from '@/types/documentExport'
import { useDeliberation } from '@/composables/useDeliberation'
import { usePersonnelListFull } from '@/composables/usePersonnelListFull'
import DeliberationDocumentPreview from './DeliberationDocumentPreview.vue'
import { getDeliberationOutcomeDocumentLabels } from '@/utils/deliberationDocumentLabels'

interface DocumentOption {
	id: string
	title: string
	description: string
}

interface SignatureMemberOption {
	personnelId: number
	name: string
	matieres: string
}

const props = defineProps<{
	modelValue: boolean
	deliberation: Deliberation | null
}>()

const emit = defineEmits<{
	'update:modelValue': [value: boolean]
	'download': [payload: DeliberationDocumentsDownloadPayload]
}>()

const { getTableauDeliberationExport } = useDeliberation()
const { fetchPersonnels, personnels } = usePersonnelListFull()

const dialogOpen = computed({
	get: () => props.modelValue,
	set: (value) => emit('update:modelValue', value)
})

const selectedDocuments = ref<string[]>([])
const downloading = ref(false)
const previewDialogOpen = ref(false)
const previewDocumentType = ref<string | null>(null)

const tableauSignaturesLoading = ref(false)
const tableauSignaturesError = ref<string | null>(null)
const includePresident = ref(true)
const selectedPersonnelIds = ref<number[]>([])
const presidentPersonnelId = ref<number | null>(null)
const signatureMemberOptions = ref<SignatureMemberOption[]>([])

const availableDocuments = computed((): DocumentOption[] => {
	const labels = getDeliberationOutcomeDocumentLabels(props.deliberation?.type_deliberation)
	return [
		{
			id: 'resultats',
			title: 'Résultats de la session de délibération',
			description: labels.resultatsModalDescription,
		},
		{
			id: 'rattrapage',
			title: labels.feuilleModalTitle,
			description: labels.feuilleModalDescription,
		},
		{
			id: 'tableau',
			title: 'Tableau de délibération',
			description: 'Tableau officiel des résultats par UE (session validée)',
		},
	]
})

const presidentDisplayName = computed(() => {
	if (!presidentPersonnelId.value) return ''
	const p = personnels.value.find((item) => item.id === presidentPersonnelId.value)
	if (!p) return ''
	const nom = p.personne?.nom || ''
	const prenom = p.personne?.prenom || ''
	return `${nom} ${prenom}`.trim()
})

function buildTableauExportFilter(): TableauDeliberationExportFilter {
	return {
		includePresident: includePresident.value,
		selectedPersonnelIds: [...selectedPersonnelIds.value],
	}
}

/** Filtres signataires pour l'aperçu — undefined tant que la liste n'est pas chargée (évite d'exclure tout le monde). */
const previewTableauExportFilter = computed((): TableauDeliberationExportFilter | undefined => {
	if (previewDocumentType.value !== 'tableau') return undefined
	if (signatureMemberOptions.value.length === 0) return undefined
	return buildTableauExportFilter()
})

function resetTableauSignatures() {
	tableauSignaturesLoading.value = false
	tableauSignaturesError.value = null
	includePresident.value = true
	selectedPersonnelIds.value = []
	presidentPersonnelId.value = null
	signatureMemberOptions.value = []
}

function personnelDisplayName(personnelId: number): string {
	const p = personnels.value.find((item) => item.id === personnelId)
	if (!p) return `Professeur #${personnelId}`
	const nom = p.personne?.nom || ''
	const prenom = p.personne?.prenom || ''
	return `${nom} ${prenom}`.trim()
}

async function loadTableauSignatures() {
	if (!props.deliberation) return

	tableauSignaturesLoading.value = true
	tableauSignaturesError.value = null

	try {
		await fetchPersonnels()
		const exportRes = await getTableauDeliberationExport(props.deliberation.id)

		if (!exportRes.success || !exportRes.data) {
			tableauSignaturesError.value = exportRes.message || 'Impossible de charger les signataires'
			return
		}

		const payload = exportRes.data
		presidentPersonnelId.value = payload.president_jury

		signatureMemberOptions.value = (payload.signature_members || []).map((member) => ({
			personnelId: member.personnel_id,
			name: personnelDisplayName(member.personnel_id),
			matieres: (member.matieres || []).join(', '),
		}))

		selectedPersonnelIds.value = signatureMemberOptions.value.map((m) => m.personnelId)
		includePresident.value = true
	} catch (e: any) {
		console.error(e)
		tableauSignaturesError.value = e?.message || 'Erreur lors du chargement des signataires'
	} finally {
		tableauSignaturesLoading.value = false
	}
}

function closeDialog() {
	dialogOpen.value = false
	selectedDocuments.value = []
	resetTableauSignatures()
}

async function previewDocument(docId: string) {
	if (docId === 'tableau' && signatureMemberOptions.value.length === 0 && !tableauSignaturesLoading.value) {
		await loadTableauSignatures()
	}
	previewDocumentType.value = docId
	previewDialogOpen.value = true
}

async function downloadSelectedDocuments() {
	if (selectedDocuments.value.length === 0) return
	
	downloading.value = true
	try {
		const payload: DeliberationDocumentsDownloadPayload = {
			documentIds: [...selectedDocuments.value],
		}

		// N'applique le filtre que si les signataires ont été chargés (évite selectedPersonnelIds=[] → tout exclu)
		if (selectedDocuments.value.includes('tableau') && signatureMemberOptions.value.length > 0) {
			payload.tableauOptions = buildTableauExportFilter()
		}

		emit('download', payload)
		closeDialog()
	} finally {
		downloading.value = false
	}
}

watch(
	() => props.modelValue,
	(newVal) => {
		if (newVal) {
			selectedDocuments.value = []
			resetTableauSignatures()
		}
	}
)

watch(
	() => selectedDocuments.value.includes('tableau'),
	(includeTableau) => {
		if (includeTableau && props.deliberation && signatureMemberOptions.value.length === 0 && !tableauSignaturesLoading.value) {
			loadTableauSignatures()
		}
	}
)
</script>

<style scoped>
.modal-header-primary {
	background-color: #417985 !important;
	color: #fff !important;
	border-radius: 3px 3px 0 0;
}
.modal-header-primary-text {
	color: #fff !important;
}
.modal-header-primary-icon .v-icon {
	color: #fff !important;
}
</style>
